import { useState, useEffect } from 'react';
import {
  List,
  Datagrid,
  TextField,
  FunctionField,
  ExportButton,
  TopToolbar,
  useNotify,
  DateInput,
  useRecordContext,
} from 'react-admin';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Avatar,
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PhoneIcon from '@mui/icons-material/Phone';

const API_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:3000';

const BalanceCard = () => {
  const [balance, setBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const notify = useNotify();

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/admin/sms/balance`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        if (data.success) {
          setBalance(data.balance);
        }
      } catch (error) {
        notify('Bakiye alınamadı', { type: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchBalance();
  }, [notify]);

  return (
    <Card
      sx={{
        background: 'linear-gradient(118deg, #7367f0, rgba(115, 103, 240, 0.7))',
        mb: 3,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography
              variant="overline"
              sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500, letterSpacing: '0.05em' }}
            >
              HeroSMS Bakiye
            </Typography>
            {loading ? (
              <CircularProgress size={32} sx={{ color: 'white', mt: 1 }} />
            ) : (
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'white', mt: 0.5 }}>
                ${balance?.toFixed(2) || '0.00'}
              </Typography>
            )}
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 1 }}>
              Mevcut hesap bakiyeniz
            </Typography>
          </Box>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              backgroundColor: 'rgba(255,255,255,0.2)',
            }}
          >
            <AccountBalanceWalletIcon sx={{ fontSize: 40, color: 'white' }} />
          </Avatar>
        </Box>
      </CardContent>
      <Box
        sx={{
          position: 'absolute',
          right: -50,
          bottom: -50,
          width: 200,
          height: 200,
          borderRadius: '50%',
          backgroundColor: 'rgba(255,255,255,0.1)',
        }}
      />
    </Card>
  );
};

const historyFilters = [
  <DateInput key="start" label="Başlangıç Tarihi" source="start" alwaysOn />,
  <DateInput key="end" label="Bitiş Tarihi" source="end" alwaysOn />,
];

const ListActions = () => (
  <TopToolbar>
    <ExportButton
      sx={{
        backgroundColor: '#7367f0',
        color: 'white',
        '&:hover': { backgroundColor: '#5549d9' },
      }}
    />
  </TopToolbar>
);

const StatusChip = () => {
  const record = useRecordContext();
  if (!record) return null;

  const statusMap: Record<string, { label: string; bg: string; color: string }> = {
    '1': { label: 'Bekliyor', bg: 'rgba(255, 159, 67, 0.12)', color: '#ff9f43' },
    '2': { label: 'Kod Bekleniyor', bg: 'rgba(0, 207, 232, 0.12)', color: '#00cfe8' },
    '3': { label: 'Tamamlandı', bg: 'rgba(40, 199, 111, 0.12)', color: '#28c76f' },
    '4': { label: 'İptal', bg: 'rgba(234, 84, 85, 0.12)', color: '#ea5455' },
    '5': { label: 'Timeout', bg: 'rgba(103, 109, 125, 0.12)', color: '#676d7d' },
    '6': { label: 'Tamamlandı', bg: 'rgba(40, 199, 111, 0.12)', color: '#28c76f' },
    '8': { label: 'İptal', bg: 'rgba(234, 84, 85, 0.12)', color: '#ea5455' },
  };

  const status = statusMap[record.status] || { label: record.status, bg: 'rgba(103, 109, 125, 0.12)', color: '#676d7d' };

  return (
    <Chip
      label={status.label}
      size="small"
      sx={{
        backgroundColor: status.bg,
        color: status.color,
        fontWeight: 600,
        fontSize: '0.7rem',
      }}
    />
  );
};

const PhoneInfo = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Avatar
        sx={{
          width: 32,
          height: 32,
          backgroundColor: 'rgba(115, 103, 240, 0.12)',
        }}
      >
        <PhoneIcon sx={{ fontSize: 16, color: '#7367f0' }} />
      </Avatar>
      <Typography variant="body2" sx={{ fontWeight: 500, color: '#d0d2d6', fontFamily: 'monospace' }}>
        {record.phone}
      </Typography>
    </Box>
  );
};

const SmsInfo = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Box sx={{ maxWidth: 200 }}>
      {record.sms ? (
        <Typography
          variant="body2"
          sx={{
            color: '#d0d2d6',
            fontSize: '0.8rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {record.sms}
        </Typography>
      ) : (
        <Typography variant="body2" sx={{ color: '#676d7d', fontStyle: 'italic', fontSize: '0.8rem' }}>
          SMS yok
        </Typography>
      )}
    </Box>
  );
};

const CostField = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Typography
      variant="body2"
      sx={{
        fontWeight: 600,
        color: record.cost > 0 ? '#28c76f' : '#676d7d',
      }}
    >
      ${record.cost?.toFixed(2) || '0.00'}
    </Typography>
  );
};

export const HistoryList = () => (
  <Box>
    <Box sx={{ mb: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#d0d2d6' }}>
        Geçmiş
      </Typography>
      <Typography variant="body2" sx={{ color: '#676d7d', mt: 0.5 }}>
        SMS aktivasyon geçmişini görüntüleyin
      </Typography>
    </Box>

    <BalanceCard />

    <List
      filters={historyFilters}
      actions={<ListActions />}
      sort={{ field: 'date', order: 'DESC' }}
      sx={{
        '& .RaList-main': {
          backgroundColor: '#283046',
          borderRadius: '12px',
          overflow: 'hidden',
        },
      }}
    >
      <Datagrid
        bulkActionButtons={false}
        sx={{
          '& .RaDatagrid-headerCell': {
            backgroundColor: '#242b3d',
            fontWeight: 600,
            color: '#676d7d',
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          },
          '& .RaDatagrid-row:hover': {
            backgroundColor: '#242b3d',
          },
          '& .RaDatagrid-rowCell': {
            borderColor: '#404656',
          },
        }}
      >
        <TextField source="id" label="ID" />
        <TextField source="date" label="Tarih" />
        <FunctionField label="Telefon" render={() => <PhoneInfo />} />
        <FunctionField label="SMS" render={() => <SmsInfo />} />
        <FunctionField label="Maliyet" render={() => <CostField />} />
        <FunctionField label="Durum" render={() => <StatusChip />} />
      </Datagrid>
    </List>
  </Box>
);
